import React, { useState, useEffect, useMemo, useCallback, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classes from './DealerStockContent.module.scss';
import Loading from '../Layout/Loading';
import EmptyContentCard from '../Layout/EmptyContentCard';
import DealerStockFilterPanel from './DealerStockFilterPanel';
import DealerStockCard from './DealerStockCard';
import SavedSearches from '../AuctionContent/SavedSearches';
import AuthContext from '../../store/auth-context';
import { dealerSavedSearchAdapter, dealerCurrentSearch } from '../../helper/dealerSavedSearches';
import {
    fetchDealerStockCars,
    fetchDealerStockFilterOptions,
    sortCarsClientSide,
} from '../../services/dealerStock';
import { trackDealerSearch } from '../../services/dealerAnalytics';
import {
    EMPTY_FILTERS,
    SORT_OPTIONS,
    PAGE_SIZE_OPTIONS,
    readStateFromSearch,
    writeStateToSearch,
    describeActiveFilters,
    FILTER_CHIP_KEYS,
} from '../../helper/dealerStockFilters';

const CONTACT_URL = 'https://www.importjdmmagic.com/contact-us';

const DealerStockContent = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // THE URL IS THE SOURCE OF TRUTH for filters, search, sort and pagination.
    //
    // Deriving state from location.search (rather than holding it only in
    // component state) is what makes a filtered grid shareable, bookmarkable
    // and back-button-navigable — all three of which the marketing site has and
    // this page did not. It also means an inbound link like
    // /dealer-stock?make=Nissan&model=Skyline lands pre-filtered.
    const urlState = useMemo(() => readStateFromSearch(location.search), [location.search]);
    const { filters, search: searchQuery, sortOrder, page, pageSize } = urlState;

    // The sidebar edits a DRAFT; only "Apply Filters" commits it to the URL.
    // Without this, every select fired a request and the grid thrashed while a
    // user was still assembling a multi-filter search.
    const [draft, setDraft] = useState(filters);
    const [searchInput, setSearchInput] = useState(searchQuery);
    const [panelOpen, setPanelOpen] = useState(false);

    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [filterOptions, setFilterOptions] = useState({
        makes: [], models: [], years: [], colors: [], bodyTypes: [], chassisGenerations: [],
    });
    const [optionsLoading, setOptionsLoading] = useState(true);

    const isFirstRender = useRef(true);
    const optionsFetchedOnce = useRef(false);
    const resultsHeadingRef = useRef(null);

    const { userData } = useContext(AuthContext);
    const [savedDealerSearches, setSavedDealerSearches] = useState(userData?.savedDealerSearches || []);
    useEffect(() => {
        setSavedDealerSearches(userData?.savedDealerSearches || []);
    }, [userData]);

    // Resync the sidebar from the URL when the APPLIED FILTERS change — a
    // back/forward navigation, a dismissed chip, an inbound deep link.
    //
    // Keyed on the serialized filters rather than on location.search, because
    // search/sort/page also live in the query string and resyncing on those
    // would throw away filter edits the user had made but not yet applied:
    // set a make and a year, type a search, press enter, and the sidebar
    // silently reverted. Those three change the URL without changing the
    // filters, so the draft is left alone.
    const appliedFilterKey = JSON.stringify(filters);
    const lastAppliedFilterKey = useRef(appliedFilterKey);
    useEffect(() => {
        if (lastAppliedFilterKey.current === appliedFilterKey) return;
        lastAppliedFilterKey.current = appliedFilterKey;
        setDraft(JSON.parse(appliedFilterKey));
    }, [appliedFilterKey]);

    // The search box mirrors the committed query, which only moves on submit
    // or when a chip clears it — so it can track location.search directly.
    useEffect(() => {
        setSearchInput(searchQuery);
    }, [searchQuery]);

    const pushState = useCallback(
        (next) => {
            const merged = {
                filters, search: searchQuery, sortOrder, page, pageSize, ...next,
            };
            const target = `/dealer-stock${writeStateToSearch(merged)}`;
            // Re-selecting the value that is already active is a no-op, not a
            // navigation. Pushing it anyway stacked identical history entries,
            // so Back appeared to do nothing until the user pressed it as many
            // times as they had re-picked the option.
            if (target === `${location.pathname}${location.search}`) return;
            navigate(target);
        },
        [navigate, location.pathname, location.search, filters, searchQuery, sortOrder, page, pageSize]
    );

    // Filter options CASCADE: re-fetched whenever the make/model the user is
    // LOOKING AT changes, so the Model, Year, Colour, Body Type and Chassis
    // lists only offer values that exist for that selection.
    //
    // Keyed on the DRAFT, not on the applied filters. The backend narrows
    // `models` to the requested make, so keying on the applied make meant that
    // picking a new make in the sidebar left the option list scoped to the OLD
    // one — the panel then filtered a Nissan-only list for `make === 'Toyota'`
    // and the Model select collapsed to nothing until you pressed Apply.
    useEffect(() => {
        let active = true;
        // Only the very first fetch shows the spinner. The panel swaps its
        // whole control set for the spinner, so doing this on every make change
        // would unmount the select the user just operated and drop their focus.
        if (!optionsFetchedOnce.current) setOptionsLoading(true);

        fetchDealerStockFilterOptions({ make: draft.make, model: draft.model })
            .then((options) => {
                if (!active) return;
                setFilterOptions((prev) => ({
                    // `makes` is always the full list — narrowing it would strand
                    // the user on whatever make they had already chosen.
                    makes: options.makes.length ? options.makes : prev.makes,
                    models: options.models,
                    years: options.years,
                    colors: options.colors,
                    bodyTypes: options.bodyTypes,
                    chassisGenerations: options.chassisGenerations,
                }));
            })
            .finally(() => {
                if (!active) return;
                optionsFetchedOnce.current = true;
                setOptionsLoading(false);
            });
        return () => { active = false; };
    }, [draft.make, draft.model]);

    // Results.
    useEffect(() => {
        let active = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const applied = JSON.parse(appliedFilterKey);
                const data = await fetchDealerStockCars({
                    ...applied,
                    search: searchQuery, page, limit: pageSize,
                });
                if (!active) return;
                setCars(data.cars || []);
                setTotalCount(data.totalCount || 0);
                setTotalPages(data.totalPages || 0);
                // A search or filter, counted for the dealer analytics with how
                // many cars it found. Page turns of the same search are not new
                // searches; the unfiltered grid is not a search at all.
                if (page === 1) {
                    trackDealerSearch({ query: searchQuery, filters: applied, resultCount: data.totalCount || 0 });
                }
            } catch (err) {
                if (!active) return;
                console.error('Error loading dealer stock:', err);
                setError('Failed to load dealer stock. Please try again later.');
                setCars([]);
                setTotalCount(0);
                setTotalPages(0);
            } finally {
                if (active) setLoading(false);
            }
        };
        load();
        return () => { active = false; };
        // Keyed on the SERIALIZED filters, not the object. readStateFromSearch
        // builds a fresh object on every location.search change, so depending on
        // `filters` refired this whole request — spinner, teardown and all — for
        // a sort change that only touches the client-side ordering.
    }, [appliedFilterKey, searchQuery, page, pageSize]);

    // Scroll back to the top on a page change — but never on first paint, which
    // would yank a deep-linked visitor down the page.
    //
    // It has to be document.body, not window. index.scss gives html AND body
    // `height: 100%` with `body { overflow-y: scroll }`, so BODY is the
    // scrolling element in this app and the document itself never overflows —
    // window.scrollTo() is a silent no-op here. Both are called because the
    // fallback costs nothing if that ever changes.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        document.body.scrollTo({ top: 0, behavior: 'smooth' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [page]);

    const sortedCars = useMemo(
        () => sortCarsClientSide(cars, sortOrder),
        [cars, sortOrder]
    );

    const activeChips = useMemo(
        () => describeActiveFilters(filters, searchQuery),
        [filters, searchQuery]
    );

    const handleDraftChange = useCallback((key, value) => {
        setDraft((prev) => {
            const next = { ...prev, [key]: value };
            // A new make invalidates whatever model was chosen under the old one.
            if (key === 'make') next.model = '';
            return next;
        });
    }, []);

    const applyFilters = () => {
        // Collapsing the panel on mobile unmounts the button that was focused,
        // which drops focus to <body> and sends the next Tab back to the top of
        // the page. Hand focus to the results heading instead.
        const wasOpen = panelOpen;
        setPanelOpen(false);
        pushState({ filters: draft, page: 1 });
        if (wasOpen && resultsHeadingRef.current) {
            resultsHeadingRef.current.focus();
        }
    };

    const resetFilters = () => {
        setDraft(EMPTY_FILTERS);
        setSearchInput('');
        navigate('/dealer-stock');
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        pushState({ search: searchInput.trim(), page: 1 });
    };

    const removeChip = (key) => {
        const keys = FILTER_CHIP_KEYS[key] || [key];
        if (key === 'search') {
            setSearchInput('');
            pushState({ search: '', page: 1 });
            return;
        }
        const next = { ...filters };
        keys.forEach((k) => { next[k] = ''; });
        setDraft(next);
        pushState({ filters: next, page: 1 });
    };

    const goToPage = (p) => {
        if (p >= 1 && p <= totalPages && p !== page) pushState({ page: p });
    };

    const pageNumbers = useMemo(() => {
        const nums = [];
        const maxToShow = 5;
        if (totalPages <= maxToShow) {
            for (let i = 1; i <= totalPages; i++) nums.push(i);
            return nums;
        }
        let start = Math.max(1, page - Math.floor(maxToShow / 2));
        let end = Math.min(totalPages, start + maxToShow - 1);
        if (end === totalPages) start = Math.max(1, end - maxToShow + 1);
        if (start > 1) {
            nums.push(1);
            if (start > 2) nums.push('...');
        }
        for (let i = start; i <= end; i++) nums.push(i);
        if (end < totalPages) {
            if (end < totalPages - 1) nums.push('...');
            nums.push(totalPages);
        }
        return nums;
    }, [page, totalPages]);

    return (
        <div className={classes.dealerStock}>
            {/* No visible heading: the navbar already says where you are, and the
                title, blurb and garage link pushed the grid a screen down. The
                h1 stays for screen readers; My Garage holds saved cars. */}
            <h1 className={classes.srOnly}>Dealer Stock</h1>

            {/* Full width, above the sidebar and the results alike. */}
            <form className={classes.searchForm} onSubmit={handleSearchSubmit}>
                <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search by make, model, year, body type..."
                    aria-label="Search dealer stock"
                />
                <button type="submit" aria-label="Search">
                    <i className="fas fa-search" aria-hidden="true"></i>
                </button>
            </form>

            <div className={classes.layout}>
                <div className={classes.sidebar}>
                    <DealerStockFilterPanel
                        draft={draft}
                        filterOptions={filterOptions}
                        optionsLoading={optionsLoading}
                        onDraftChange={handleDraftChange}
                        onApply={applyFilters}
                        onReset={resetFilters}
                        open={panelOpen}
                        onToggle={() => setPanelOpen((o) => !o)}
                        savedSearches={
                            <SavedSearches
                                savedSearches={savedDealerSearches}
                                setSavedSearches={setSavedDealerSearches}
                                adapter={dealerSavedSearchAdapter}
                                current={dealerCurrentSearch(filters, searchQuery)}
                                onApply={(saved) => navigate(`/dealer-stock${saved.query ? `?${saved.query}` : ''}`)}
                            />
                        }
                    />
                </div>

                <div className={classes.results}>
                    <div className={classes.resultsBar}>
                        <div className={classes.resultsMeta} ref={resultsHeadingRef} tabIndex={-1}>
                            {loading ? (
                                <span>Loading vehicles...</span>
                            ) : (
                                <span>
                                    Showing <strong>{sortedCars.length}</strong> of{' '}
                                    <strong>{totalCount.toLocaleString()}</strong> vehicles
                                    {searchQuery && <> for <strong>&ldquo;{searchQuery}&rdquo;</strong></>}
                                    {totalPages > 1 && (
                                        <>
                                            {' '}
                                            <span className={classes.pageOf}>
                                                &middot; Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                                            </span>
                                        </>
                                    )}
                                </span>
                            )}
                        </div>

                        <div className={classes.resultsControls}>
                            <div className={classes.controlGroup}>
                                <label htmlFor="ds-sort">Sort by:</label>
                                <select
                                    id="ds-sort"
                                    value={sortOrder}
                                    onChange={(e) => pushState({ sortOrder: e.target.value, page: 1 })}
                                >
                                    {SORT_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div className={classes.controlGroup}>
                                <label htmlFor="ds-page-size">Show:</label>
                                <select
                                    id="ds-page-size"
                                    value={pageSize}
                                    onChange={(e) => pushState({ pageSize: Number(e.target.value), page: 1 })}
                                >
                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>{size} per page</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {activeChips.length > 0 && (
                        <div className={classes.chipRow}>
                            <span className={classes.chipRowLabel}>Active filters:</span>
                            {activeChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className={classes.chip}
                                    onClick={() => removeChip(chip.key)}
                                    aria-label={`Remove filter ${chip.label}`}
                                >
                                    {chip.label}
                                    <i className="fas fa-times" aria-hidden="true"></i>
                                </button>
                            ))}
                            <button type="button" className={classes.clearAll} onClick={resetFilters}>
                                Clear all
                            </button>
                        </div>
                    )}

                    {error ? (
                        <div className={classes.error}>{error}</div>
                    ) : loading ? (
                        <div className={classes.stateContainer}>
                            <Loading text="Dealer Stock" />
                        </div>
                    ) : sortedCars.length === 0 ? (
                        <div className={classes.stateContainer}>
                            {/* An out-of-range page is a different dead end from a
                                filter that matches nothing: there ARE results, just
                                not on this page, and the pagination controls below
                                are hidden by this very branch — so without an
                                explicit way back the only escape was editing the
                                URL by hand. */}
                            {totalCount > 0 && page > 1 ? (
                                <>
                                    <EmptyContentCard
                                        header="Nothing on this page"
                                        message={`There ${totalCount === 1 ? 'is' : 'are'} ${totalCount.toLocaleString()} matching ${totalCount === 1 ? 'vehicle' : 'vehicles'}, but page ${page} is past the end of the results.`}
                                    />
                                    <button
                                        type="button"
                                        className={classes.backToStartButton}
                                        onClick={() => pushState({ page: 1 })}
                                    >
                                        Back to page 1
                                    </button>
                                </>
                            ) : (
                                <EmptyContentCard
                                    header="No vehicles found"
                                    message="No dealer stock matches your search. Try adjusting your filters or check back later."
                                />
                            )}
                        </div>
                    ) : (
                        <>
                            <div className={classes.grid}>
                                {sortedCars.map((car) => (
                                    <DealerStockCard key={car._id} car={car} />
                                ))}
                            </div>

                            {totalPages > 1 && (
                                <div className={classes.pagination}>
                                    <button
                                        onClick={() => goToPage(page - 1)}
                                        disabled={page === 1}
                                        aria-label="Previous page"
                                    >
                                        <i className="fas fa-chevron-left" aria-hidden="true"></i>
                                    </button>
                                    {pageNumbers.map((p, i) =>
                                        p === '...' ? (
                                            <span key={`e-${i}`} className={classes.ellipsis}>...</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => goToPage(p)}
                                                className={p === page ? classes.activePage : ''}
                                                aria-current={p === page ? 'page' : undefined}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}
                                    <button
                                        onClick={() => goToPage(page + 1)}
                                        disabled={page === totalPages}
                                        aria-label="Next page"
                                    >
                                        <i className="fas fa-chevron-right" aria-hidden="true"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <section className={classes.ctaBand}>
                <h2>Can&rsquo;t find what you&rsquo;re looking for?</h2>
                <p>
                    We source vehicles to order from Japanese dealers and auctions every week.
                    Tell us the car you want and we&rsquo;ll hunt it down.
                </p>
                <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                    Request a Vehicle
                </a>
            </section>
        </div>
    );
};

export default DealerStockContent;
